import * as React from 'react';
import {
  ACTION_OFFERING_TYPE_FILTER_APPLIED,
  ACTION_PRICE_FILTER_APPLIED,
  ACTION_UNIQUE_EXP_FILTER_APPLIED,
  CATEGORY_CITYWIDE_SEARCH,
} from 'utils/analytics/eventTypesConstants';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogForm,
  DialogHeading,
  Divider,
  Typography,
} from 'omakase';
import { Formik, FormikProps } from 'formik';
import { OfferingSearchTypeOrBusinessType } from 'utils/searchBar';
import { SearchFiltersFields } from 'components/www/availability-search/SearchHeader';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer } from '@tock/coretypes';
import {
  dialogContent,
  dividers,
  priceRangeFilter,
  sectionHeading,
} from 'components/www/availability-search/filters/searchFiltersCssDefs';
import { useCuisineFilters } from 'hooks/search/useCuisineFilters';
import { useExperienceTagAggregations } from 'hooks/search/useExperienceTagAggregations';
import SearchFilterCuisines from 'components/www/availability-search/filters/SearchFilterCuisines';
import SearchFilterExperienceTags, {
  ExperienceTags,
} from 'components/www/availability-search/filters/SearchFilterExperienceTags';
import SearchFilterPriceRange, {
  priceRangeDefaultValue,
} from 'components/www/availability-search/filters/SearchFilterPriceRange';
import SearchFilterSeatingAreaTags from 'components/www/availability-search/filters/SearchFilterSeatingAreaTags';
import SearchFilterType from 'components/www/availability-search/filters/SearchFilterType';

export type SearchFilterType =
  | 'OFFERING_TYPE'
  | 'PRICE'
  | 'SEATING_AREA'
  | 'UNIQUE_EXPERIENCES'
  | 'CUISINES';

export interface SearchFiltersDialogProps {
  isOpen: () => boolean;
  onClose: () => void;
  selectedCuisines: string[];
  selectedType?: OfferingSearchTypeOrBusinessType;
  selectedPriceRange?: string;
  selectedExperienceTags: ExperienceTags;
  onSubmit: (values: SearchFiltersFields) => void;
  aggregations?: consumer.Aggregations;
  visibleSearchFilters: SearchFilterType[];
  dialogTitle: string;
}

export const OFFERING_TYPE_HEADER_ID = 'offering-type-header';
export const PRICE_HEADER_ID = 'price-header';

const SearchFiltersDialog: React.FunctionComponent<SearchFiltersDialogProps> = ({
  isOpen,
  onClose,
  selectedCuisines,
  selectedType,
  selectedPriceRange,
  selectedExperienceTags,
  onSubmit,
  aggregations,
  visibleSearchFilters,
  dialogTitle,
}) => {
  const { tagAggregationsInternal } = useExperienceTagAggregations(
    selectedExperienceTags,
    aggregations
  );
  const [cuisineAggregationsInternal, setCuisineAggregationsInternal] = React.useState<
    consumer.Aggregations | undefined
  >(aggregations);
  const { cuisineFiltersEnabled, sendCuisineGAEvents } = useCuisineFilters();

  const sendAnalyticsEvents = (values: SearchFiltersFields) => {
    const { cuisines, experienceTags, priceRange, type } = values;

    // Offering type
    window.analytics.sendEvent(CATEGORY_CITYWIDE_SEARCH, ACTION_OFFERING_TYPE_FILTER_APPLIED, type);

    // Price range
    if (priceRange !== priceRangeDefaultValue) {
      window.analytics.sendEvent(CATEGORY_CITYWIDE_SEARCH, ACTION_PRICE_FILTER_APPLIED, priceRange);
    }

    // Unique experiences
    [].concat([], ...Object.values(experienceTags)).forEach((tag) => {
      window.analytics.sendEvent(CATEGORY_CITYWIDE_SEARCH, ACTION_UNIQUE_EXP_FILTER_APPLIED, tag);
    });

    // Cuisines
    sendCuisineGAEvents(cuisines);
  };

  const handleSubmit = (values: SearchFiltersFields) => {
    const valuesInternal: SearchFiltersFields = {
      ...values,
      priceRange: values.priceRange === '-1' ? undefined : values.priceRange,
    };
    sendAnalyticsEvents(values);
    onSubmit(valuesInternal);
    onClose();
  };

  const dialogHeading = 'search-filters-dialog-heading';

  const generateFilterFromVisibleFilterType = (
    type: SearchFilterType,
    values: SearchFiltersFields
  ) => {
    switch (type) {
      case 'OFFERING_TYPE':
        return (
          <React.Fragment key={type}>
            <SearchFiltersDialogSectionHeader id={OFFERING_TYPE_HEADER_ID}>
              What are you looking for?
            </SearchFiltersDialogSectionHeader>
            <SearchFilterType values={values} />
          </React.Fragment>
        );
      case 'SEATING_AREA':
        return (
          <React.Fragment key={type}>
            <Divider css={dividers} position="middle" tall />
            <SearchFilterSeatingAreaTags />
          </React.Fragment>
        );
      case 'PRICE':
        return (
          <React.Fragment key={type}>
            <Divider css={dividers} position="middle" tall />
            <SearchFiltersDialogSectionHeader mb={3.5} id={PRICE_HEADER_ID}>
              Price
            </SearchFiltersDialogSectionHeader>
            <SearchFilterPriceRange css={priceRangeFilter} />
          </React.Fragment>
        );
      case 'UNIQUE_EXPERIENCES':
        return (
          <SearchFilterExperienceTags
            key={type}
            aggregationsInternal={tagAggregationsInternal}
            selectedTags={values.experienceTags}
          />
        );
      case 'CUISINES':
        return (
          cuisineFiltersEnabled && (
            <SearchFilterCuisines
              key={type}
              aggregations={aggregations}
              aggregationsInternal={cuisineAggregationsInternal}
              setAggregationsInternal={setCuisineAggregationsInternal}
              selectedCuisines={values.cuisines}
            />
          )
        );
      default:
        assertNever(type);
    }
  };

  return (
    <Dialog
      aria-labelledby={dialogHeading}
      variant="fullScreen"
      fullHeight
      open={isOpen()}
      onClose={onClose}
    >
      <Formik<SearchFiltersFields>
        initialValues={{
          cuisines: selectedCuisines,
          type: selectedType,
          priceRange: selectedPriceRange || '-1',
          experienceTags: selectedExperienceTags,
        }}
        onSubmit={handleSubmit}
      >
        {({ values }: FormikProps<SearchFiltersFields>) => (
          <DialogForm>
            <DialogHeading
              headingConfig={{
                left: {
                  configType: 'title',
                  title: dialogTitle,
                },
                right: {
                  configType: 'closeButton',
                },
              }}
              onClose={onClose}
              id={dialogHeading}
            />

            <DialogContent disablePadding css={dialogContent}>
              {visibleSearchFilters.map((filterType) =>
                generateFilterFromVisibleFilterType(filterType, values)
              )}
            </DialogContent>

            <DialogActions>
              <Button variant="primary" size="large" fullWidth type="submit">
                Update search
              </Button>
            </DialogActions>
          </DialogForm>
        )}
      </Formik>
    </Dialog>
  );
};

export default SearchFiltersDialog;

const SearchFiltersDialogSectionHeader: React.FunctionComponent<{ id?: string; mb?: number }> = ({
  children,
  mb = 1.5,
  id,
}) => {
  return (
    // This typography variant is meant to replicate the heading from form selection controls groups.
    // To be handled with omakase components at a later date.
    <Typography component="h3" variant="body02Strong" mb={mb} css={sectionHeading} id={id}>
      {children}
    </Typography>
  );
};
